import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
// import { examsService } from '@/services/exams.service'; // Commented out - exam dropdown hidden
import { coursesService } from '@/services/courses.service';
import { useToast } from '@/contexts/ToastContext';
import {
  ChevronRight,
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  X,
  Trash2
} from 'lucide-react';
import { DuplicateFileDialog } from './DuplicateFileDialog';
import type { UploadContext } from '@/types';
// import type { LearningResource } from '@/types'; // Commented out - not used after removing existing materials box

interface UploadWizardProps {
  onClose: () => void;
  onUploadComplete: () => void;
}

type WizardStep = 'context' | 'files' | 'metadata';

export function UploadWizard({ onClose, onUploadComplete }: UploadWizardProps) {
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState<WizardStep>('context');
  const [uploadContext, setUploadContext] = useState<Partial<UploadContext>>({
    exam_id: 'GENERIC',  // Default exam to GENERIC
    resource_type: 'apostila',
    source_authority: 'apostila',
    auto_chunk: true,
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState<{
    isOpen: boolean;
    fileName: string;
    existingDocument?: any;
    onResolve: (useExisting: boolean) => void;
  } | null>(null);

  // Commented out - exam dropdown is hidden, default is GENERIC
  /* const { data: examsData } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams();
      } catch (error) {
        console.error('Failed to fetch exams:', error);
        return [];
      }
    },
  }); */

  const { data: coursesData } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        return await coursesService.getCourses();
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        return [];
      }
    },
  });

  // Get existing materials for selected exam
  const { data: existingMaterials } = useQuery({
    queryKey: ['materials', uploadContext.exam_id],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        const { contentService } = await import('@/services/content.service');
        const response = await contentService.getDocuments({ exam: uploadContext.exam_id }, token);
        return response.documents || [];
      } catch (error) {
        console.error('Failed to fetch materials:', error);
        return [];
      }
    },
    enabled: !!uploadContext.exam_id,
  });

  // Filter courses by selected exam - Commented out since course dropdown is hidden
  /* const filteredCourses = uploadContext.exam_id && coursesData
    ? coursesData.filter((c: any) => c.linked_exams.includes(uploadContext.exam_id!))
    : []; */

  // Check for version conflicts
  const versionConflicts = selectedFiles.map(file => {
    const similarMaterials = existingMaterials?.filter(m =>
      m.title.toLowerCase().includes(file.name.split('.')[0].toLowerCase().slice(0, 10)) &&
      m.is_active !== false
    ) || [];
    return { file, conflicts: similarMaterials };
  }).filter(item => item.conflicts.length > 0);

  const handleFileAdd = (files: FileList | null) => {
    if (!files) return;
    const validFiles = Array.from(files).filter(file => {
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'video/mp4',
        'video/quicktime'
      ];
      return validTypes.includes(file.type);
    });
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const canProceedToFiles = uploadContext.exam_id && uploadContext.resource_type && uploadContext.source_authority;
  const canProceedToMetadata = selectedFiles.length > 0;

  const handleUpload = async () => {
    if (!uploadContext.exam_id || selectedFiles.length === 0) return;

    setIsUploading(true);
    let successCount = 0;
    let duplicateCount = 0;
    let errorCount = 0;

    try {
      const token = localStorage.getItem('accessToken') || '';
      const contentService = await import('@/services/content.service');

      // Bulk upload
      for (const file of selectedFiles) {
        try {
          // Find course name if course_id is selected
          const selectedCourse = uploadContext.course_id
            ? coursesData?.find((c: any) => c.course_id === uploadContext.course_id)
            : null;

          await contentService.contentService.uploadDocument(file, {
            title: file.name,
            exam: uploadContext.exam_id!,
            course: uploadContext.course_id,  // Course ID
            courseName: selectedCourse?.name,  // Course name
            category: 'study-guide', // Default category
            version: new Date().getFullYear().toString(),
            language: 'pt-BR',
            publisher: 'ANBIMA',
            publishedDate: new Date().toISOString().split('T')[0],
            description: ''
          }, token);
          successCount++;
        } catch (error: any) {
          // Check if it's a duplicate file error
          if (error?.response?.data?.isDuplicate) {
            const existingDoc = error.response.data.existingDocument;

            // Show duplicate dialog and wait for user decision
            const userDecision = await new Promise<boolean>((resolve) => {
              setDuplicateDialog({
                isOpen: true,
                fileName: file.name,
                existingDocument: existingDoc,
                onResolve: resolve
              });
            });

            // Dialog is already closed by button handlers

            if (userDecision) {
              // User chose to use existing file
              duplicateCount++;
              showToast(
                'success',
                'Using Existing File',
                `Using existing file "${existingDoc?.title || file.name}".`
              );
              // File already exists, so we count it as handled
              successCount++;
            } else {
              // User cancelled
              showToast(
                'info',
                'Upload Cancelled',
                `Upload of "${file.name}" was cancelled.`
              );
              // Don't count as error, just skip
              continue;
            }
          } else {
            errorCount++;
            showToast(
              'error',
              'Upload Failed',
              `Failed to upload "${file.name}": ${error?.response?.data?.error || error.message}`
            );
          }
        }
      }

      // Show summary message
      if (successCount > 0) {
        const newUploads = successCount - duplicateCount;
        let message = '';

        if (newUploads > 0 && duplicateCount > 0) {
          message = `Successfully processed ${successCount} file${successCount > 1 ? 's' : ''} (${newUploads} new, ${duplicateCount} existing)`;
        } else if (newUploads > 0) {
          message = `Successfully uploaded ${newUploads} file${newUploads > 1 ? 's' : ''}`;
        } else if (duplicateCount > 0) {
          message = `Using ${duplicateCount} existing file${duplicateCount > 1 ? 's' : ''}`;
        }

        if (errorCount > 0) {
          message += `, ${errorCount} failed`;
        }

        showToast(
          'success',
          'Upload Complete',
          message + '.'
        );
      }

      if (successCount > 0 || duplicateCount > 0) {
        onUploadComplete();
        onClose();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('error', 'Upload Error', 'An unexpected error occurred during upload.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Upload Learning Materials</h2>
            <p className="text-sm text-gray-600 mt-1">
              Select exam/course context first, then upload files for better indexing
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-center justify-center space-x-4">
            <StepIndicator
              number={1}
              title="Context"
              isActive={currentStep === 'context'}
              isCompleted={!!canProceedToFiles}
            />
            <ChevronRight className="w-5 h-5 text-gray-400" />
            <StepIndicator
              number={2}
              title="Files"
              isActive={currentStep === 'files'}
              isCompleted={canProceedToMetadata}
            />
            <ChevronRight className="w-5 h-5 text-gray-400" />
            <StepIndicator
              number={3}
              title="Review"
              isActive={currentStep === 'metadata'}
              isCompleted={false}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 'context' && (
            <ContextStep
              examsData={[]}
              filteredCourses={[]}
              uploadContext={uploadContext}
              setUploadContext={setUploadContext}
            />
          )}

          {currentStep === 'files' && (
            <FilesStep
              selectedFiles={selectedFiles}
              handleFileAdd={handleFileAdd}
              handleRemoveFile={handleRemoveFile}
              versionConflicts={versionConflicts}
            />
          )}

          {currentStep === 'metadata' && (
            <MetadataStep
              uploadContext={uploadContext}
              setUploadContext={setUploadContext}
              selectedFiles={selectedFiles}
              versionConflicts={versionConflicts}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <div className="flex space-x-3">
            {currentStep !== 'context' && (
              <button
                onClick={() => setCurrentStep(prev =>
                  prev === 'metadata' ? 'files' : 'context'
                )}
                className="btn-secondary"
              >
                Back
              </button>
            )}
            {currentStep === 'context' && (
              <button
                onClick={() => setCurrentStep('files')}
                disabled={!canProceedToFiles}
                className="btn-primary"
              >
                Next: Select Files
              </button>
            )}
            {currentStep === 'files' && (
              <button
                onClick={() => setCurrentStep('metadata')}
                disabled={!canProceedToMetadata}
                className="btn-primary"
              >
                Next: Review
              </button>
            )}
            {currentStep === 'metadata' && (
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="btn-primary"
              >
                {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Duplicate File Dialog */}
      {duplicateDialog && (
        <DuplicateFileDialog
          isOpen={duplicateDialog.isOpen}
          duplicateInfo={{
            fileName: duplicateDialog.fileName,
            existingDocument: {
              title: duplicateDialog.existingDocument?.title,
              fileSize: duplicateDialog.existingDocument?.fileSize,
              uploadedAt: duplicateDialog.existingDocument?.uploadedAt,
              exam: duplicateDialog.existingDocument?.exam,
            }
          }}
          onUseExisting={() => {
            // Close dialog immediately
            const resolve = duplicateDialog.onResolve;
            setDuplicateDialog(null);
            // Then resolve the promise
            if (resolve) {
              resolve(true);
            }
          }}
          onCancel={() => {
            // Close dialog immediately
            const resolve = duplicateDialog.onResolve;
            setDuplicateDialog(null);
            // Then resolve the promise
            if (resolve) {
              resolve(false);
            }
          }}
        />
      )}
    </div>
  );
}

function StepIndicator({ number, title, isActive, isCompleted }: {
  number: number;
  title: string;
  isActive: boolean;
  isCompleted: boolean;
}) {
  return (
    <div className="flex items-center space-x-2">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
          isCompleted
            ? 'bg-green-600 text-white'
            : isActive
            ? 'bg-primary-600 text-white'
            : 'bg-gray-300 text-gray-600'
        }`}
      >
        {isCompleted ? <CheckCircle className="w-5 h-5" /> : number}
      </div>
      <span className={`text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
        {title}
      </span>
    </div>
  );
}

function ContextStep({
  // examsData, // Commented out - not used since exam dropdown is hidden
  // filteredCourses, // Commented out - not used since course dropdown is hidden
  uploadContext,
  // setUploadContext, // Commented out - not used after commenting out resource type and source authority dropdowns
  // existingMaterials, // Commented out - not used after removing existing materials box
}: any) {
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900 font-medium">
          📌 Context-First Upload for Better RAG Indexing
        </p>
        <p className="text-xs text-blue-700 mt-1">
          Selecting exam and course first allows the system to:
        </p>
        <ul className="text-xs text-blue-700 mt-2 ml-4 list-disc space-y-1">
          <li>Pre-map materials to correct topics during chunking</li>
          <li>Generate better embeddings with exam context</li>
          <li>Faster indexing with pre-defined metadata</li>
          <li>Detect version conflicts before upload</li>
        </ul>
      </div>

      {/* Exam dropdown - Commented out, default is GENERIC */}
      {/* <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Exam * <span className="text-red-500">(Required)</span>
        </label>
        <select
          value={uploadContext.exam_id || ''}
          onChange={(e) => setUploadContext({ ...uploadContext, exam_id: e.target.value, course_id: undefined })}
          className="input-field"
          required
        >
          <option value="">Select exam...</option>
          {examsData?.map((exam: any) => (
            <option key={exam.exam_id} value={exam.exam_id}>
              {exam.exam_id} - {exam.name}
            </option>
          ))}
        </select>
      </div> */}

      {/* Display default exam */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Exam (Default)
        </label>
        <p className="text-base font-semibold text-blue-900">GENERIC</p>
        <p className="text-xs text-gray-600 mt-1">All materials will be uploaded to the GENERIC exam.</p>
      </div>

      {uploadContext.exam_id && (
        <>
          {/* Course dropdown - Commented out */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course (Optional)
            </label>
            <select
              value={uploadContext.course_id || ''}
              onChange={(e) => setUploadContext({ ...uploadContext, course_id: e.target.value || undefined })}
              className="input-field"
            >
              <option value="">No course (exam-level material)</option>
              {filteredCourses.map((course: any) => (
                <option key={course.course_id} value={course.course_id}>
                  {course.course_id} - {course.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {filteredCourses.length} course(s) available for {uploadContext.exam_id}
            </p>
          </div> */}

          {/* Resource Type and Source Authority dropdowns - Commented out */}
          {/* <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resource Type *
              </label>
              <select
                value={uploadContext.resource_type}
                onChange={(e) => setUploadContext({ ...uploadContext, resource_type: e.target.value as any })}
                className="input-field"
              >
                <option value="apostila">Apostila</option>
                <option value="official_doc">Official Document</option>
                <option value="book">Book</option>
                <option value="slides">Slides</option>
                <option value="video">Video</option>
                <option value="transcript">Transcript</option>
                <option value="article">Article</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Authority *
              </label>
              <select
                value={uploadContext.source_authority}
                onChange={(e) => setUploadContext({ ...uploadContext, source_authority: e.target.value as any })}
                className="input-field"
              >
                <option value="official">Official</option>
                <option value="apostila">Apostila</option>
                <option value="supplementary">Supplementary</option>
                <option value="reference">Reference</option>
              </select>
            </div>
          </div> */}

          {/* Display default Resource Type and Source Authority - Commented out */}
          {/* <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resource Type (Default)
              </label>
              <p className="text-base font-semibold text-blue-900 capitalize">{uploadContext.resource_type}</p>
              <p className="text-xs text-gray-600 mt-1">All materials will use this resource type.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Authority (Default)
              </label>
              <p className="text-base font-semibold text-blue-900 capitalize">{uploadContext.source_authority}</p>
              <p className="text-xs text-gray-600 mt-1">All materials will use this source authority.</p>
            </div>
          </div> */}

          {/* Existing Materials box - Commented out */}
          {/* {existingMaterials.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm font-medium text-yellow-900 mb-2">
                📚 Existing Materials for {uploadContext.exam_id}
              </p>
              <div className="space-y-2">
                {existingMaterials.slice(0, 5).map((material: LearningResource) => (
                  <div key={material.resource_id} className="flex items-center justify-between text-xs">
                    <span className="text-yellow-800">{material.title}</span>
                    <span className="text-yellow-600">
                      {material.version || 'v1.0'} • {material.chunk_count || 0} chunks
                    </span>
                  </div>
                ))}
                {existingMaterials.length > 5 && (
                  <p className="text-xs text-yellow-600">
                    +{existingMaterials.length - 5} more materials
                  </p>
                )}
              </div>
            </div>
          )} */}
        </>
      )}
    </div>
  );
}

function FilesStep({ selectedFiles, handleFileAdd, handleRemoveFile, versionConflicts }: any) {
  return (
    <div className="space-y-6">
      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors cursor-pointer"
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium text-gray-900 mb-2">
          Drop files here or click to browse
        </p>
        <p className="text-sm text-gray-600">
          PDF, DOCX, PPTX, MP4, MOV (up to 500MB each)
        </p>
        <input
          id="file-input"
          type="file"
          multiple
          onChange={(e) => handleFileAdd(e.target.files)}
          className="hidden"
          accept=".pdf,.docx,.pptx,.mp4,.mov"
        />
      </div>

      {selectedFiles.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Selected Files ({selectedFiles.length})
          </h3>
          <div className="space-y-2">
            {selectedFiles.map((file: File, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3 flex-1">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="ml-4 text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {versionConflicts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900 mb-2">
                ⚠️ Version Conflicts Detected
              </p>
              <p className="text-xs text-yellow-700 mb-3">
                Similar materials already exist. Set version info in the next step to manage versions.
              </p>
              {versionConflicts.map((item: any, idx: number) => (
                <div key={idx} className="text-xs text-yellow-800 mb-1">
                  • {item.file.name} → Similar to {item.conflicts.length} existing material(s)
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetadataStep({ uploadContext, setUploadContext, selectedFiles, versionConflicts }: any) {
  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Upload Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Exam:</span>
            <span className="ml-2 font-medium">{uploadContext.exam_id}</span>
          </div>
          <div>
            <span className="text-gray-600">Course:</span>
            <span className="ml-2 font-medium">{uploadContext.course_id || 'None'}</span>
          </div>
          <div>
            <span className="text-gray-600">Type:</span>
            <span className="ml-2 font-medium">{uploadContext.resource_type}</span>
          </div>
          <div>
            <span className="text-gray-600">Authority:</span>
            <span className="ml-2 font-medium">{uploadContext.source_authority}</span>
          </div>
          <div className="col-span-2">
            <span className="text-gray-600">Files:</span>
            <span className="ml-2 font-medium">{selectedFiles.length} file(s)</span>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Version (Optional)
        </label>
        <input
          type="text"
          value={uploadContext.version || ''}
          onChange={(e) => setUploadContext({ ...uploadContext, version: e.target.value })}
          className="input-field"
          placeholder="e.g., 2026.1, v2.0, Jan2026"
        />
        <p className="text-xs text-gray-500 mt-1">
          Set version to track material updates. Leave empty for auto-versioning.
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="auto-chunk"
          checked={uploadContext.auto_chunk !== false}
          onChange={(e) => setUploadContext({ ...uploadContext, auto_chunk: e.target.checked })}
          className="w-4 h-4 text-primary-600"
        />
        <label htmlFor="auto-chunk" className="text-sm text-gray-700">
          Automatically chunk materials after upload (recommended for RAG)
        </label>
      </div>

      {versionConflicts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-900 mb-2">
            ⚠️ Action Required: Version Conflicts
          </p>
          <p className="text-xs text-red-700 mb-3">
            When uploading new versions, the system will:
          </p>
          <ul className="text-xs text-red-700 ml-4 list-disc space-y-1">
            <li>Mark old materials as inactive (is_active = false)</li>
            <li>Deprecate old chunks (removed from RAG retrieval)</li>
            <li>Link old → new versions (superseded_by field)</li>
            <li>Keep old chunks in database for audit trail</li>
          </ul>
        </div>
      )}

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm font-medium text-green-900 mb-2">
          ✓ Ready to Upload
        </p>
        <p className="text-xs text-green-700">
          All materials will be indexed with the selected context for optimal RAG performance.
        </p>
      </div>
    </div>
  );
}
